import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Printer, Info } from "@phosphor-icons/react";
import { openPrintWindow } from "../lib/printWindow";

// Local-only settings (per browser/device). Each printing device stores its own preferences.
const KEY = "fichaje_printer_settings";
const DEFAULTS = {
  autoPrint: false,
  headerText: "",
  footerText: "¡Gracias!",
  copies: 1,
};

export function loadPrinterSettings() {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY)) || {}) };
  } catch {
    return { ...DEFAULTS };
  }
}

export default function AdminPrinters() {
  const [s, setS] = useState(DEFAULTS);
  const [testPrinting, setTestPrinting] = useState(false);

  useEffect(() => {
    setS(loadPrinterSettings());
  }, []);

  const save = () => {
    localStorage.setItem(KEY, JSON.stringify(s));
    toast.success("Ajustes de impresión guardados en este dispositivo");
  };

  const testPrint = () => {
    setTestPrinting(true);
    const html = `<html><head><title>Prueba impresión</title>
      <style>body{font-family:-apple-system,sans-serif;padding:16px;font-size:14px}
      h1{font-size:18px;margin:0 0 6px}hr{border:none;border-top:1px dashed #999;margin:10px 0}
      .small{font-size:11px;color:#666}</style></head><body>
      <h1>${s.headerText || "Bar Sense Nom"}</h1>
      <div class="small">Prueba de impresión — ${new Date().toLocaleString("es-ES")}</div>
      <hr/>
      <p><b>Mesa:</b> 7</p>
      <p>2 × Aletes picantones</p>
      <p>1 × Blanco Nieva</p>
      <hr/>
      <p><b>Total:</b> 6,60 €</p>
      <p class="small">${s.footerText || ""}</p>
      </body></html>`;
    const win = openPrintWindow(html, { features: "width=380,height=520" });
    if (!win) {
      toast.error("Bloqueado por el navegador. Permite ventanas emergentes.");
      setTestPrinting(false);
      return;
    }
    setTimeout(() => {
      try { win.close(); } catch (err) { console.debug("printer test close: window already closed", err); }
      setTestPrinting(false);
    }, 1200);
  };

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 mb-2">
        <Printer size={24} weight="duotone" className="text-[var(--primary)]" />
        <h1 className="font-display text-4xl font-bold tracking-tight">Impresoras</h1>
      </div>
      <p className="text-[var(--text-secondary)] mb-8">
        Configura cómo se imprimen los pedidos que llegan de la carta.
      </p>

      <div className="card-flat p-6 space-y-5">
        <div className="flex items-start gap-3 text-sm bg-[var(--bg)] border border-[var(--border)] rounded-lg p-4">
          <Info size={20} weight="duotone" className="text-[var(--primary)] shrink-0 mt-0.5" />
          <div className="text-[var(--text-secondary)]">
            La app usa la <b>impresora predeterminada de tu sistema</b> (la que
            Windows tiene marcada por defecto). Si quieres cambiarla, hazlo desde
            <em> Panel de control → Dispositivos e impresoras → Establecer como
            predeterminada</em>. También puedes elegirla en el cuadro de diálogo
            cada vez que se imprima.
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-[var(--border)] pt-4">
          <div>
            <Label className="font-display font-semibold">
              Auto-imprimir pedidos nuevos
            </Label>
            <p className="text-[var(--text-secondary)] text-xs mt-1">
              Al llegar un pedido de la carta, se abre el diálogo de impresión
              automáticamente en este dispositivo.
            </p>
          </div>
          <Switch
            checked={s.autoPrint}
            onCheckedChange={(v) => setS({ ...s, autoPrint: v })}
            data-testid="printer-auto-print"
          />
        </div>

        <div>
          <Label className="label-cap">Cabecera del ticket</Label>
          <Input
            value={s.headerText}
            onChange={(e) => setS({ ...s, headerText: e.target.value })}
            placeholder="Bar Sense Nom"
            className="mt-2"
            data-testid="printer-header"
          />
        </div>

        <div>
          <Label className="label-cap">Pie del ticket</Label>
          <Input
            value={s.footerText}
            onChange={(e) => setS({ ...s, footerText: e.target.value })}
            placeholder="¡Gracias!"
            className="mt-2"
            data-testid="printer-footer"
          />
        </div>

        <div>
          <Label className="label-cap">Copias por pedido</Label>
          <Input
            type="number"
            min="1"
            max="5"
            value={s.copies}
            onChange={(e) =>
              setS({ ...s, copies: Math.max(1, Math.min(5, parseInt(e.target.value) || 1)) })
            }
            className="mt-2 w-24"
            data-testid="printer-copies"
          />
          <p className="text-[var(--text-secondary)] text-xs mt-1">
            Útil si quieres una copia para cocina y otra para barra.
          </p>
        </div>

        <div className="flex gap-3 pt-3">
          <Button
            className="btn-primary"
            onClick={save}
            data-testid="save-printer-btn"
          >
            Guardar
          </Button>
          <Button
            variant="outline"
            onClick={testPrint}
            disabled={testPrinting}
            data-testid="test-print-btn"
          >
            <Printer size={16} className="mr-2" />
            {testPrinting ? "Imprimiendo…" : "Prueba de impresión"}
          </Button>
        </div>
      </div>
    </div>
  );
}
